import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Video } from "@/types/video";
import { formatCount } from "@/utils/format";
import { categories } from "@/mocks/categories";

interface HeroCarouselProps {
  videos: Video[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (videos.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % videos.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [videos.length]);

  if (videos.length === 0) return null;

  const go = (index: number) => setActive((index + videos.length) % videos.length);

  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative h-[440px] w-full md:h-[600px]">
        {videos.map((video, index) => {
          const categoryName =
            categories.find((item) => item.key === video.category)?.name ?? "热门";
          return (
            <div
              key={video.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === active ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
            >
              <img
                src={video.cover}
                alt={`${video.title} 精选封面`}
                title={`${video.title} - 91短视频`}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/25"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30"></div>

              <div className="mx-auto flex h-full w-full max-w-7xl items-center px-4 md:px-6">
                <div className="w-full max-w-2xl">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                    <i className="ri-fire-fill"></i>
                    {categoryName} · 精选推荐
                  </span>
                  {index === 0 ? (
                    <h1 className="mt-4 text-2xl font-bold leading-tight text-background-50 md:text-4xl">
                      {video.title}
                    </h1>
                  ) : (
                    <h2 className="mt-4 text-2xl font-bold leading-tight text-background-50 md:text-4xl">
                      {video.title}
                    </h2>
                  )}
                  <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                    {video.description}
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-200">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-play-circle-line text-base"></i>
                      {formatCount(video.plays)} 次播放
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-user-3-line text-base"></i>
                      {video.author}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line text-base"></i>
                      {video.duration}
                    </span>
                  </div>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/detail/${video.id}`)}
                      className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
                    >
                      <i className="ri-play-fill text-lg"></i>
                      立即观看
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/category/${video.category}`)}
                      className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-background-50/15"
                    >
                      浏览同类视频
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => go(active - 1)}
        aria-label="上一个"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur transition hover:bg-foreground-950/70 md:flex md:left-6"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(active + 1)}
        aria-label="下一个"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur transition hover:bg-foreground-950/70 md:flex md:right-6"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {videos.map((video, index) => (
          <button
            key={video.id}
            type="button"
            onClick={() => go(index)}
            aria-label={`切换到第 ${index + 1} 个推荐`}
            className={`h-2 cursor-pointer rounded-full transition-all ${
              index === active ? "w-7 bg-primary-500" : "w-2 bg-background-50/60"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}